// ─── ConflictViewRouter ─────────────────────────────────────────────────────
//
// Maps entity_type to the correct per-entity conflict view component.
// Eliminates duplicated if/else chains in SyncStatusPage (POS) and
// AdminSyncHealthPage (Admin).
//
// Traceability: FR-SYNC-013 (conflict resolution screen).

import { BillConflictView } from './BillConflictView';
import { QuotationConflictView } from './QuotationConflictView';
import { ProductConflictView } from './ProductConflictView';
import { CustomerConflictView } from './CustomerConflictView';
import { ExpenseConflictView } from './ExpenseConflictView';
import { StockAdjustmentConflictView } from './StockAdjustmentConflictView';
import { GenericDiffTable } from './GenericDiffTable';

export interface ConflictViewRouterProps {
  entityType: string;
  clientVersion: Record<string, unknown>;
  serverVersion: Record<string, unknown>;
  deviceId: string | null;
  conflictMessage?: string;
}

const ENTITY_VIEW_MAP: Record<
  string,
  React.ComponentType<{
    clientVersion: Record<string, unknown>;
    serverVersion: Record<string, unknown>;
    deviceId: string | null;
    conflictMessage?: string;
  }>
> = {
  BILL: BillConflictView,
  QUOTATION: QuotationConflictView,
  PRODUCT: ProductConflictView,
  CUSTOMER: CustomerConflictView,
  EXPENSE: ExpenseConflictView,
  STOCK_ADJUSTMENT: StockAdjustmentConflictView,
};

/** Set of entity types that have dedicated conflict views (render their own headers). */
export const ENTITY_VIEWS = new Set(Object.keys(ENTITY_VIEW_MAP));

export function ConflictViewRouter({
  entityType,
  clientVersion,
  serverVersion,
  deviceId,
  conflictMessage,
}: ConflictViewRouterProps) {
  const ViewComponent = ENTITY_VIEW_MAP[entityType];

  if (ViewComponent) {
    return (
      <ViewComponent
        clientVersion={clientVersion}
        serverVersion={serverVersion}
        deviceId={deviceId}
        conflictMessage={conflictMessage}
      />
    );
  }

  return (
    <GenericDiffTable
      clientVersion={clientVersion}
      serverVersion={serverVersion}
    />
  );
}
